import React from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// Canadian Nutrition Facts table / Valeur nutritive
//
// Distinct component rather than a mode on the FDA <NutritionLabel> — the two
// tables differ in nutrient set, order, which rows carry a % Daily Value, the
// footnote, and the units convention, so branching inside one component would
// be mostly branches.
//
// Values arrive already rounded under Food and Drug Regulations B.01.401 (see
// applyCanadianRounding in backend/services/nutritionCalcService.js). This file
// only lays them out — no rounding or DV math here, so there's exactly one place
// where the regulatory arithmetic lives.
//
// Canadian typographic conventions applied here: a space between number and unit
// ("9 g", not "9g") and a space before the percent sign ("12 %").
// ─────────────────────────────────────────────────────────────────────────────

const NBSP = ' ';

// Row labels. In a bilingual table each row shows both languages separated by " / ".
// Where the two languages share a word (Sodium, Potassium, Calcium) Canada prints it
// once rather than duplicating it.
const L = {
  fat:          { en: 'Fat',          fr: 'Lipides' },
  saturated:    { en: 'Saturated',    fr: 'saturés' },
  trans:        { en: 'Trans',        fr: 'trans' },
  carbohydrate: { en: 'Carbohydrate', fr: 'Glucides' },
  fibre:        { en: 'Fibre',        fr: 'Fibres' },
  sugars:       { en: 'Sugars',       fr: 'Sucres' },
  protein:      { en: 'Protein',      fr: 'Protéines' },
  cholesterol:  { en: 'Cholesterol',  fr: 'Cholestérol' },
  sodium:       { en: 'Sodium',       fr: 'Sodium' },
  potassium:    { en: 'Potassium',    fr: 'Potassium' },
  calcium:      { en: 'Calcium',      fr: 'Calcium' },
  iron:         { en: 'Iron',         fr: 'Fer' },
};

const label = (key, mode) => {
  const { en, fr } = L[key];
  if (mode === 'en') return en;
  if (mode === 'fr') return fr;
  return en === fr ? en : `${en} / ${fr}`;
};

// "9 g" / "520 mg" — em dash when the value is absent so a blank never reads as zero.
const amount = (val, unit) =>
  val === null || val === undefined ? '—' : `${val}${NBSP}${unit}`;

// No space before "%" — matches the wording quoted in Health Canada's Directory of
// Nutrition Facts Table Formats ("5% or less is a little" / "5% ou moins c'est peu").
const pct = (val) => (val === null || val === undefined ? '' : `${val}%`);

export default function CanadianNutritionLabel({
  values,
  dv,
  servingEn,
  servingFr,
  servingsPerContainer,
  mode = 'bilingual',
  compact = false,
}) {
  if (!values) return null;
  const v = values;
  const d = dv || {};

  const width = compact ? 250 : 300;
  const base = compact ? 10 : 11;

  const box = {
    fontFamily: 'Helvetica, Arial, sans-serif',
    border: '1px solid #000',
    padding: compact ? '5px 7px' : '7px 9px',
    width: `${width}px`,
    fontSize: `${base}px`,
    lineHeight: 1.25,
    color: '#000',
    background: '#fff',
    boxSizing: 'border-box',
  };

  const thick = { borderBottom: '5px solid #000' };
  const medium = { borderBottom: '2.5px solid #000' };
  const hair = { borderBottom: '0.5px solid #000' };

  // A nutrient line: name + amount on the left, %DV right-aligned.
  const Row = ({ name, value, unit, dvPct, bold, indent, noBorder }) => (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
      paddingLeft: indent ? `${compact ? 10 : 12}px` : 0,
      paddingTop: '1px', paddingBottom: '1px',
      ...(noBorder ? {} : hair),
    }}>
      <span style={{ fontWeight: bold ? 'bold' : 'normal' }}>
        {name} <strong>{amount(value, unit)}</strong>
      </span>
      {dvPct != null && <span style={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>{pct(dvPct)}</span>}
    </div>
  );

  const title = mode === 'fr' ? 'Valeur nutritive'
    : mode === 'en' ? 'Nutrition Facts'
      : null;

  const dvHeader = mode === 'fr' ? '% valeur quotidienne*'
    : mode === 'en' ? '% Daily Value*'
      : null;

  // Footnote — the Canadian interpretive statement, not the FDA's 2,000-calorie note.
  // Wording quoted verbatim from Health Canada's Directory of Nutrition Facts Table
  // Formats (Figure 3.1(B)); the leading asterisk ties it to the %DV column heading.
  const footEn = '*5% or less is a little, 15% or more is a lot';
  const footFr = "*5% ou moins c'est peu, 15% ou plus c'est beaucoup";

  return (
    <div style={box}>
      {/* Title */}
      {mode === 'bilingual' ? (
        <div style={{ ...hair, paddingBottom: '1px' }}>
          <div style={{ fontSize: `${compact ? 19 : 23}px`, fontWeight: 800, lineHeight: 1.05 }}>
            Nutrition Facts
          </div>
          <div style={{ fontSize: `${compact ? 15 : 18}px`, fontWeight: 800, lineHeight: 1.05 }}>
            Valeur nutritive
          </div>
        </div>
      ) : (
        <div style={{
          fontSize: `${compact ? 19 : 23}px`, fontWeight: 800, lineHeight: 1.05,
          ...hair, paddingBottom: '1px',
        }}>
          {title}
        </div>
      )}

      {/* Serving size — "Per 1 slice (55 g)" / "pour 1 tranche (55 g)" */}
      <div style={{ fontSize: `${base}px`, fontWeight: 'bold', paddingTop: '2px' }}>
        {(mode === 'bilingual' || mode === 'en') && servingEn && <div>Per {servingEn}</div>}
        {(mode === 'bilingual' || mode === 'fr') && servingFr && <div>pour {servingFr}</div>}
        {!servingEn && !servingFr && <div style={{ color: '#b00' }}>Serving size not set</div>}
      </div>

      {servingsPerContainer != null && (
        <div style={{ fontSize: `${base - 1}px` }}>
          {mode !== 'fr' && <span>{servingsPerContainer} servings per container</span>}
          {mode === 'bilingual' && <span> / </span>}
          {mode !== 'en' && <span>{servingsPerContainer} portions par contenant</span>}
        </div>
      )}

      <div style={{ ...thick, marginTop: '2px' }} />

      {/* Calories */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
        ...medium, paddingTop: '1px', paddingBottom: '1px',
      }}>
        <span style={{ fontWeight: 800, fontSize: `${compact ? 14 : 16}px` }}>Calories</span>
        <span style={{ fontWeight: 800, fontSize: `${compact ? 14 : 16}px` }}>
          {v.calories == null ? '—' : v.calories}
        </span>
      </div>

      {/* % Daily Value column header */}
      <div style={{ textAlign: 'right', fontWeight: 'bold', ...hair, fontSize: `${base - 1}px` }}>
        {mode === 'bilingual' ? (
          <>
            <div>% Daily Value*</div>
            <div>% valeur quotidienne*</div>
          </>
        ) : dvHeader}
      </div>

      {/* Fat block */}
      <Row name={label('fat', mode)} value={v.fat} unit="g" dvPct={d.fat} bold />
      {/* Saturated and trans share one combined %DV, printed against the trans line
          so it visually spans the pair — the standard Canadian presentation. */}
      <Row name={label('saturated', mode)} value={v.saturatedFat} unit="g" indent noBorder />
      <Row
        name={`+ ${label('trans', mode)}`}
        value={v.transFat}
        unit="g"
        dvPct={d.saturatedTrans}
        indent
      />

      {/* Carbohydrate block — Canada places this before cholesterol/sodium, and
          declares carbohydrate and protein with no % Daily Value at all. */}
      <Row name={label('carbohydrate', mode)} value={v.carbohydrate} unit="g" bold />
      <Row name={label('fibre', mode)} value={v.fibre} unit="g" dvPct={d.fibre} indent />
      <Row name={label('sugars', mode)} value={v.sugars} unit="g" dvPct={d.sugars} indent />

      <Row name={label('protein', mode)} value={v.protein} unit="g" bold />
      <Row name={label('cholesterol', mode)} value={v.cholesterol} unit="mg" bold />
      <Row name={label('sodium', mode)} value={v.sodium} unit="mg" dvPct={d.sodium} bold />

      <div style={{ ...thick }} />

      {/* Mandatory minerals. Vitamin D is not part of Canada's core table. */}
      <Row name={label('potassium', mode)} value={v.potassium} unit="mg" dvPct={d.potassium} />
      <Row name={label('calcium', mode)} value={v.calcium} unit="mg" dvPct={d.calcium} />
      <Row name={label('iron', mode)} value={v.iron} unit="mg" dvPct={d.iron} noBorder />

      <div style={{ borderTop: '5px solid #000', paddingTop: '2px', fontSize: `${base - 2}px`, lineHeight: 1.2 }}>
        {(mode === 'bilingual' || mode === 'en') && <div>{footEn}</div>}
        {(mode === 'bilingual' || mode === 'fr') && <div>{footFr}</div>}
      </div>
    </div>
  );
}
